import { MasterOption } from "../types/masters";
import { Pagination } from "../types/pagination";
import { RentalDocument } from "../types/rental-opportunity";
import {
  HomPropertyItem,
  NearbyPlace,
  NEW_SAMPLE_PROPERTY,
  RentalProperty,
  RentalPropertyConfig,
  RentalPropertyItem,
  Sight,
} from "../types/rental-property";
import {
  ChecklistTicket,
  PropertyChecklistListPayload,
} from "../types/rental-property-checklist";
import {
  RentalPropertyHOApprovalDetail,
  RentalPropertyHOApprovalListItem,
  RentalPropertyHOApprovalsListPayload,
  RentalPropertyHOApprovalsListQuery,
  RentalPropertyHOApprovalsPaginate,
  unwrapPropertyHOApprovalDetailPayload,
} from "../types/rental-property-ho-approval";
import {
  IRentalPropertyService,
  NEW_PROPERTY_ADD_KEYS,
  NewRentalProperty,
} from "../types/services/rental-property-service";
import { DELETE, FetchResult, GET, PATCH, POST } from "../utils/fetch";

type RentalPropertyListPaginated = {
  properties: RentalPropertyItem[];
  paginate: Pagination;
};

type HomPropertyListPaginated = {
  properties: HomPropertyItem[];
  paginate: Pagination;
};

const newPropertyAddKeysSet = new Set(NEW_PROPERTY_ADD_KEYS);

function normalizePaginate(raw: unknown): RentalPropertyHOApprovalsPaginate {
  if (!raw || typeof raw !== "object") {
    return {
      total_records: 0,
      total_pages: 1,
      current_page: 1,
    };
  }
  const p = raw as Record<string, unknown>;
  const totalRecords = Number(p.total_records) || 0;
  const totalPages = Math.max(1, Number(p.total_pages) || 1);
  const currentPage = Number(p.current_page) || 1;
  return {
    total_records: totalRecords,
    total_pages: totalPages,
    current_page: currentPage,
  };
}

function normalizeApprovalsListPayload(
  raw: unknown,
): RentalPropertyHOApprovalsListPayload {
  if (!raw || typeof raw !== "object") {
    return {
      approvals: [],
      paginate: normalizePaginate(undefined),
    };
  }
  const r = raw as Record<string, unknown>;
  let approvals: RentalPropertyHOApprovalListItem[];
  if (Array.isArray(raw)) {
    approvals = raw as RentalPropertyHOApprovalListItem[];
  } else if (Array.isArray(r.approvals)) {
    approvals = r.approvals as RentalPropertyHOApprovalListItem[];
  } else {
    approvals = [];
  }
  return {
    approvals,
    paginate: normalizePaginate(r.paginate),
  };
}

function normalizeChecklistListPayload(
  raw: unknown,
): PropertyChecklistListPayload {
  if (!raw || typeof raw !== "object") {
    return {
      checklist_items: [],
      paginate: normalizePaginate(undefined),
    };
  }
  const r = raw as Record<string, unknown>;
  const checklistItems = Array.isArray(r.checklist_items)
    ? (r.checklist_items as ChecklistTicket[])
    : [];
  return {
    checklist_items: checklistItems,
    paginate: normalizePaginate(r.paginate),
  };
}

function buildApprovalsQueryString(
  query?: RentalPropertyHOApprovalsListQuery,
): string {
  if (!query) {
    return "";
  }
  const params = new URLSearchParams();
  if (query.status != null && query.status !== "") {
    params.set("status", query.status);
  }
  if (query.category_id != null && query.category_id !== "") {
    params.set("category_id", query.category_id);
  }
  if (query.page != null) {
    params.set("page", String(query.page));
  }
  if (query.per_page != null) {
    params.set("per_page", String(query.per_page));
  }
  if (query.property_slugs?.length) {
    for (const slug of query.property_slugs) {
      if (slug) {
        params.append("property_slugs[]", slug);
      }
    }
  }
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

type RentalPropertyResponse = {
  property: any;
};

type RentalPropertyConfigsResponse = {
  property_configs: any[];
};

class RentalPropertyService implements IRentalPropertyService {
  async listProperties(
    location: string,
    rooms: string,
    guest_capacity: string,
    search?: string,
    page?: string,
    page_size?: string,
  ): Promise<FetchResult<RentalPropertyItem[]>> {
    const base = "/api/v2/rental/properties",
      qp = new URLSearchParams(),
      qpOptions = [
        {
          key: "location_id",
          value: location,
        },
        {
          key: "rooms",
          value: rooms,
        },
        {
          key: "max_guests",
          value: guest_capacity,
        },
        {
          key: "name",
          value: search,
        },
        {
          key: "per_page",
          value: page_size,
        },
        {
          key: "page",
          value: page,
        },
      ];

    qpOptions.forEach(({ key, value }) => {
      if (!value) {
        return;
      }

      qp.append(key, value);
    });

    const qpM = qp.toString();
    let url = base;
    if (qpM) {
      url = `${base}?${qpM}`;
    }

    const { response, error } = await GET<RentalPropertyListPaginated>(url);

    if (error) {
      return {
        response: null,
        error,
      };
    }

    if (!response) {
      return {
        response: null,
        error: new Error("Unknown error"),
      };
    }

    return {
      response: response.properties,
      paginate: response.paginate,
      error,
    };
  }

  async listHomProperties(
    search?: string,
    location_ids?: string[],
    hom_poc_ids?: string[],
    sales_channel?: string,
    license_type?: string,
    delisted?: string,
    page?: string,
    page_size?: string,
  ): Promise<FetchResult<HomPropertyItem[]>> {
    const base = "/api/v2/rental/hom_properties",
      qp = new URLSearchParams();

    if (search) {
      qp.append("query", search);
    }
    if (location_ids?.length) {
      location_ids.forEach((id) => qp.append("location_ids[]", id));
    }
    if (hom_poc_ids?.length) {
      hom_poc_ids.forEach((id) => qp.append("hom_poc_ids[]", id));
    }
    if (sales_channel) {
      qp.append("sales_channel", sales_channel);
    }
    if (license_type) {
      qp.append("license_type", license_type);
    }
    if (delisted) {
      qp.append("delisted", delisted);
    }
    if (page) {
      qp.append("page", page);
    }
    if (page_size) {
      qp.append("per_page", page_size);
    }

    const qpM = qp.toString();
    let url = base;
    if (qpM) {
      url = `${base}?${qpM}`;
    }

    const { response, error } = await GET<HomPropertyListPaginated>(url);

    if (error) {
      return {
        response: null,
        error,
      };
    }

    if (!response) {
      return {
        response: null,
        error: new Error("Unknown error"),
      };
    }

    return {
      response: response.properties,
      paginate: response.paginate,
      error,
    };
  }

  async addNewProperty(
    property: NewRentalProperty,
  ): Promise<FetchResult<string>> {
    const keyMap: any = {
        location_category: "location_id",
        adults_count: "adults",
        children_count: "children",
        extra_adults_count: "extra_adults",
        extra_adults_fare: "adults_rate",
        extra_adults_fare_currency: "extra_person_currency",
        extra_child_fare: "children_rate",
        extra_children_count: "extra_children",
        pet_friendly: "pets_allowed",
        rentable: "rentable",
        trending_listed: "trending",
        channel_manager_id: "sf_hotel_id",
        security_deposit: "deposit",
        maintenance_days_before_checkin: "maintenance_days_before_check_in",
        maintenance_days_after_checkout: "maintenance_days_after_check_out",
      },
      formData = new FormData();

    Object.entries(property).forEach(([key, value]) => {
      if (
        value === undefined ||
        value === null ||
        !newPropertyAddKeysSet.has(key)
      ) {
        return;
      }
      formData.append(keyMap[key] || key, value.toString());
    });

    formData.set("brand_id", property.brand_id.toString());

    formData.set("villa_alias", property.alias.toString());

    if (property.publication_features) {
      property.publication_features.forEach((feature) =>
        formData.append("featured_in[]", feature),
      );
    }

    const { seo_title, seo_description, seo_keywords, seo_canonical } =
      property;

    if (seo_title !== undefined) {
      formData.set("seo_attributes[title]", seo_title);
    }

    if (seo_description !== undefined) {
      formData.set("seo_attributes[description]", seo_description);
    }

    if (seo_keywords !== undefined) {
      formData.set("seo_attributes[keywords]", seo_keywords);
    }

    if (seo_canonical !== undefined) {
      formData.set("seo_attributes[canonical]", seo_canonical);
    }

    formData.append("currency_id", "1");

    if (
      property.homeowner_email &&
      property.homeowner_name &&
      property.homeowner_email &&
      property.homeowner_phone_no
    ) {
      formData.append("home_owners_attrs[][primary]", "true");
      formData.append("home_owners_attrs[][active]", "true");
      formData.append("home_owners_attrs[][name]", property.homeowner_name);
      formData.append("home_owners_attrs[][email]", property.homeowner_email);

      const [countryCode, phoneNo] = property.homeowner_phone_no.split(" ");

      formData.append("home_owners_attrs[][country_code]", countryCode);
      formData.append("home_owners_attrs[][mobile]", phoneNo);
    }

    if (
      property.contract_type &&
      property.contract_rev_share_percentage &&
      property.contract_free_nights &&
      property.contract_effective_date &&
      property.contract_expiry_date
    ) {
      formData.set("contract_attributes[active]", "true");

      formData.append(
        "contract_attributes[license_type]",
        property.contract_type,
      );
      formData.append(
        "contract_attributes[rev_share_percentage]",
        property.contract_rev_share_percentage.toString(),
      );
      formData.append(
        "contract_attributes[permitted_nights]",
        property.contract_free_nights.toString(),
      );
      formData.append(
        "contract_attributes[effective_date]",
        property.contract_effective_date,
      );
      formData.append(
        "contract_attributes[expiry_date]",
        property.contract_expiry_date,
      );
    }

    if (property.youtube_video.id) {
      formData.set("videos_attrs[][id]", property.youtube_video.id.toString());
    }

    if (property.youtube_video.value) {
      formData.set("videos_attrs[][video_id]", property.youtube_video.value);
    }

    formData.set("rental_staff_id", property.rental_staff_id.toString());

    formData.set(
      "maintenance_staff_id",
      property.maintenance_staff_id.toString(),
    );

    formData.set(
      "guest_relation_staff_id",
      property.guest_relation_staff_id.toString(),
    );

    const url = "/api/v2/rental/properties",
      { response, error } = await POST<RentalPropertyResponse>(url, formData);

    if (error) {
      return {
        response: null,
        error,
      };
    }

    if (!response) {
      return {
        response: null,
        error: new Error("Unknown error"),
      };
    }

    const { slug } = response.property;

    return {
      response: slug,
      error,
    };
  }

  async getProperty(
    property_slug: string,
  ): Promise<FetchResult<RentalProperty>> {
    const url = `/api/v2/rental/properties/${property_slug}`,
      { response, error } = await GET<{ property: any }>(url);

    if (error) {
      return {
        response: null,
        error,
      };
    }

    if (!response) {
      return {
        response: null,
        error: new Error("unknown error"),
      };
    }

    const { property } = response;

    const rentalProperty: RentalProperty = JSON.parse(
        JSON.stringify(NEW_SAMPLE_PROPERTY),
      ),
      keyMap: Record<string, string> = {
        location_id: "location_category",
        adults: "adults_count",
        children: "children_count",
        extra_adults: "extra_adults_count",
        adults_rate: "extra_adults_fare",
        extra_person_currency: "extra_adults_fare_currency",
        children_rate: "extra_child_fare",
        extra_children: "extra_children_count",
        pets_allowed: "pet_friendly",
        rentable: "rentable",
        trending: "trending_listed",
        sf_hotel_id: "channel_manager_id",
        featured_in: "publication_features",
        rental_staff_id: "rental_staff_id",
        maintenance_staff_id: "maintenance_staff_id",
        guest_relation_staff_id: "guest_relation_staff_id",
      },
      locationMap: Record<string, string> = {
        address_line: "address_line",
        city: "city",
        state: "state",
        google_place_id: "google_location_url",
        lat: "latitude",
        long: "longitude",
      };

    for (const key in property) {
      const nativeValue = property[key],
        value = nativeValue === null ? "" : nativeValue;
      if (key in rentalProperty) {
        (rentalProperty as any)[key] = value;
      }
      if (key in keyMap) {
        (rentalProperty as any)[keyMap[key]] = value;
      }
    }

    if (property.delisted_date) {
      rentalProperty.status = "delisted";
    } else if (property.active) {
      rentalProperty.status = "active";
    } else {
      rentalProperty.status = "deactive";
    }

    for (const key in locationMap) {
      const nativeValue = property[key],
        value = nativeValue === null ? "" : nativeValue;
      (rentalProperty.location as any)[locationMap[key]] = value;
    }

    rentalProperty.property_images =
      property.images?.map((each: any) => {
        const { id, url, alt, caption, sequence, category, orientation } = each;
        return {
          id: id,
          url: url || "",
          alt: alt || "",
          caption: caption || "",
          sequence: sequence || -1,
          name: category,
          aspect_ratio: orientation || "",
        };
      }) || [];

    rentalProperty.cancellation_policies = property.cancellation_rules;

    rentalProperty.nearby_places =
      property.nearby_places?.map((each: any) => {
        const { id, name, city, estimated_time, lat, long, category, state } =
            each,
          place: NearbyPlace = {
            id: id,
            name: name || "",
            category: category || "",
            city: city || "",
            distance_from_property: estimated_time || "",
            latitude: lat || "",
            longitude: long || "",
            state: state || "",
          };

        return place;
      }) || [];

    rentalProperty.things_to_do =
      property.things_to_do?.map((each: any) => {
        const { id, name, state, image } = each,
          sight: Sight = {
            id,
            name: name || "",
            state: state || "",
            image: image?.url || "",
          };

        return sight;
      }) || [];

    const { seo, property_configs = [] } = property;

    if (seo) {
      const { id, title, description, keywords, canonical } = seo;
      rentalProperty.seo = {
        ...rentalProperty.seo,
        id: id || undefined,
        title: title || "",
        description: description || "",
        keywords: keywords || "",
        canonical: canonical || "",
      };
    }

    rentalProperty.upcoming = property.upcoming || false;

    if (property.contract) {
      const {
        effective_date,
        expiry_date,
        id,
        license_type,
        permitted_nights,
        rev_share_percentage,
      } = property.contract;

      if (id !== undefined) {
        rentalProperty.contract_id = id;
      }

      rentalProperty.contract_type = license_type || "";
      rentalProperty.contract_rev_share_percentage = rev_share_percentage || 0;
      rentalProperty.contract_free_nights = permitted_nights || 0;
      rentalProperty.contract_effective_date = effective_date || "";
      rentalProperty.contract_expiry_date = expiry_date || "";
    }

    if (
      property.rev_share_percentage !== undefined &&
      property.rev_share_percentage !== null
    ) {
      rentalProperty.rev_share_percentage =
        typeof property.rev_share_percentage === "number"
          ? property.rev_share_percentage
          : Number(property.rev_share_percentage);
    }

    if (property.home_owners?.length) {
      const [first] = property.home_owners,
        { id, name, email, country_code, mobile } = first,
        phoneNo = [country_code || "", mobile || ""].join(" ").trim();

      rentalProperty.homeowner_id = id;
      rentalProperty.homeowner_name = name;
      rentalProperty.homeowner_email = email;
      rentalProperty.homeowner_phone_no = phoneNo;
    }

    rentalProperty.security_deposit = property.deposit || 0;

    rentalProperty.maintenance_days_before_checkin =
      property.maintenance_days_before_check_in || 0;

    rentalProperty.maintenance_days_after_checkout =
      property.maintenance_days_after_check_out || 0;

    rentalProperty.rental_staff_id = property.rental_staff_id;

    rentalProperty.maintenance_staff_id = property.maintenance_staff_id;

    rentalProperty.guest_relation_staff_id = property.guest_relation_staff_id;

    if (property.ho_manager) {
      rentalProperty.ho_manager = property.ho_manager;
    }

    if (property.housekeeping_manager) {
      rentalProperty.housekeeping_manager = property.housekeeping_manager;
    }

    if (property.maintenance_manager) {
      rentalProperty.maintenance_manager = property.maintenance_manager;
    }

    rentalProperty.configOptions = property_configs.map((each: any) => {
      const option: MasterOption = {
        value: each.id.toString(),
        name: `${each.bedrooms} Bedrooms`,
      };

      return option;
    });

    rentalProperty.brand_id = property.brand_id || 0;

    rentalProperty.alias = property.villa_alias || "";

    rentalProperty.youtube_video = property.videos?.length
      ? { id: property.videos[0].id, value: property.videos[0].video_id }
      : { value: "" };

    return {
      response: rentalProperty,
      error: null,
    };
  }

  async updateProperty(
    property_slug: string,
    property: Partial<RentalProperty>,
  ): Promise<FetchResult<void>> {
    const formData = new FormData();

    if (property.amenities) {
      property.amenities.forEach((amenity) => {
        const { id } = amenity;
        formData.append("rental_amenity_ids[]", id.toString());
      });
    }

    if (property.moods) {
      property.moods.forEach((mood) => {
        const { id } = mood;
        formData.append("rental_mood_ids[]", id.toString());
      });
    }

    if (property.things_to_do) {
      property.things_to_do.forEach((thing_to_do, i) => {
        const { id, image, name, state } = thing_to_do;
        if (id !== undefined) {
          formData.append(`rental_things_todo_attrs[${i}][id]`, id.toString());
        } else {
          formData.append(
            `rental_things_todo_attrs[${i}][file]`,
            image as File,
          );
        }
        formData.append(`rental_things_todo_attrs[${i}][name]`, name);
        formData.append(`rental_things_todo_attrs[${i}][state]`, state);
      });
    }

    if (property.highlights) {
      property.highlights.forEach((highlight) => {
        const { id } = highlight;
        formData.append("rental_highlight_ids[]", id.toString());
      });
    }

    if (property.house_rules) {
      property.house_rules.forEach((house_rule) => {
        const { id, content } = house_rule;
        if (id !== undefined) {
          formData.append("house_rules_attrs[][id]", id.toString());
        }
        formData.append("house_rules_attrs[][content]", content);
      });
    }

    if (property.cancellation_policies) {
      property.cancellation_policies.forEach((cancellation_policy) => {
        const { id, content } = cancellation_policy;
        if (id !== undefined) {
          formData.append("cancellation_rules_attrs[][id]", id.toString());
        }
        formData.append("cancellation_rules_attrs[][content]", content);
      });
    }

    if (property.bedrooms) {
      property.bedrooms.forEach((bedroom) => {
        const { id, name, bed_type } = bedroom;
        if (id !== undefined) {
          formData.append("rental_bedrooms_attrs[][id]", id.toString());
        }
        formData.append("rental_bedrooms_attrs[][name]", name);
        formData.append("rental_bedrooms_attrs[][bed_type]", bed_type);
      });
    }

    if (property.property_images) {
      property.property_images.forEach((property_image, i) => {
        const { id, url, name, caption, alt, sequence, aspect_ratio } =
          property_image;

        if (id === undefined) {
          formData.append(`images_attrs[${i}][file]`, url as File);
        } else {
          formData.append(`images_attrs[${i}][id]`, id.toString());
        }

        formData.append(`images_attrs[${i}][caption]`, caption);

        formData.append(`images_attrs[${i}][alt]`, alt);

        formData.append(`images_attrs[${i}][orientation]`, aspect_ratio);

        if (sequence !== undefined) {
          formData.append(`images_attrs[${i}][sequence]`, sequence.toString());
        }

        if (name !== undefined) {
          formData.append(`images_attrs[${i}][category]`, name);
        }
      });
    }

    if (property.nearby_places) {
      property.nearby_places.forEach((nearby_place) => {
        const {
          id,
          name,
          city,
          state,
          category,
          distance_from_property,
          latitude,
          longitude,
        } = nearby_place;
        if (id !== undefined) {
          formData.append("rental_nearby_places_attrs[][id]", id.toString());
        }
        formData.append("rental_nearby_places_attrs[][name]", name);
        formData.append("rental_nearby_places_attrs[][city]", city);
        formData.append("rental_nearby_places_attrs[][state]", state);
        formData.append("rental_nearby_places_attrs[][category]", category);
        formData.append(
          "rental_nearby_places_attrs[][estimated_time]",
          distance_from_property,
        );
        formData.append("rental_nearby_places_attrs[][lat]", latitude);
        formData.append("rental_nearby_places_attrs[][long]", longitude);
      });
    }

    if (property.publication_features?.length) {
      property.publication_features.forEach((feature) =>
        formData.append("featured_in[]", feature),
      );
    } else {
      formData.set("featured_in[]", "");
    }

    if (property.seo) {
      const {
        id: seo_id,
        title: seo_title,
        description: seo_description,
        keywords: seo_keywords,
        canonical: seo_canonical,
      } = property.seo;
      if (seo_id) {
        formData.set("seo_attributes[id]", seo_id.toString());
      }
      if (seo_title !== undefined) {
        formData.set("seo_attributes[title]", seo_title);
      }
      if (seo_description !== undefined) {
        formData.set("seo_attributes[description]", seo_description);
      }
      if (seo_keywords !== undefined) {
        formData.set("seo_attributes[keywords]", seo_keywords);
      }
      if (seo_canonical !== undefined) {
        formData.set("seo_attributes[canonical]", seo_canonical);
      }
    }

    if (
      property.homeowner_email &&
      property.homeowner_name &&
      property.homeowner_email &&
      property.homeowner_phone_no
    ) {
      if (property.homeowner_id) {
        formData.append(
          "home_owners_attrs[][id]",
          property.homeowner_id.toString(),
        );
      }

      formData.append("home_owners_attrs[][primary]", "true");
      formData.append("home_owners_attrs[][active]", "true");
      formData.append("home_owners_attrs[][name]", property.homeowner_name);
      formData.append("home_owners_attrs[][email]", property.homeowner_email);

      const [countryCode, phoneNo] = property.homeowner_phone_no.split(" ");

      formData.append("home_owners_attrs[][country_code]", countryCode);
      formData.append("home_owners_attrs[][mobile]", phoneNo);
    }

    if (
      property.contract_type !== undefined &&
      property.contract_rev_share_percentage !== undefined &&
      property.contract_free_nights !== undefined &&
      property.contract_effective_date !== undefined &&
      property.contract_expiry_date !== undefined
    ) {
      if (property.contract_id) {
        formData.append(
          "contract_attributes[id]",
          property.contract_id.toString(),
        );
      }
      formData.set("contract_attributes[active]", "true");
      formData.append(
        "contract_attributes[license_type]",
        property.contract_type,
      );
      formData.append(
        "contract_attributes[rev_share_percentage]",
        property.contract_rev_share_percentage.toString(),
      );
      formData.append(
        "contract_attributes[permitted_nights]",
        property.contract_free_nights.toString(),
      );
      formData.append(
        "contract_attributes[effective_date]",
        property.contract_effective_date,
      );
      formData.append(
        "contract_attributes[expiry_date]",
        property.contract_expiry_date,
      );
    }

    if (property.rental_staff_id !== undefined) {
      formData.set("rental_staff_id", property.rental_staff_id.toString());
    }

    if (property.maintenance_staff_id !== undefined) {
      formData.set(
        "maintenance_staff_id",
        property.maintenance_staff_id.toString(),
      );
    }

    if (property.guest_relation_staff_id !== undefined) {
      formData.set(
        "guest_relation_staff_id",
        property.guest_relation_staff_id.toString(),
      );
    }

    if (property.ho_manager?.id !== undefined) {
      formData.set("ho_manager_id", property.ho_manager.id.toString());
    }

    if (property.housekeeping_manager?.id !== undefined) {
      formData.set(
        "housekeeping_manager_id",
        property.housekeeping_manager.id.toString(),
      );
    }

    if (property.maintenance_manager?.id !== undefined) {
      formData.set(
        "maintenance_manager_id",
        property.maintenance_manager.id.toString(),
      );
    }

    if (property.brand_id !== undefined) {
      formData.set("brand_id", property.brand_id.toString());
    }

    if (property.alias !== undefined) {
      formData.set("villa_alias", property.alias.toString());
    }

    if (property.youtube_video?.id) {
      formData.set("videos_attrs[][id]", property.youtube_video.id.toString());
    }

    if (property.youtube_video?.value) {
      formData.set("videos_attrs[][video_id]", property.youtube_video.value);
    }

    const keyMap: Record<string, string> = {
      location_category: "location_id",
      adults_count: "adults",
      children_count: "children",
      extra_adults_count: "extra_adults",
      extra_adults_fare: "adults_rate",
      extra_adults_fare_currency: "extra_person_currency",
      extra_child_fare: "children_rate",
      extra_children_count: "extra_children",
      pet_friendly: "pets_allowed",
      rentable: "rentable",
      trending_listed: "trending",
      google_location_url: "google_place_id",
      latitude: "lat",
      longitude: "long",
      channel_manager_id: "sf_hotel_id",
      upcoming: "upcoming",
      security_deposit: "deposit",
      maintenance_days_before_checkin: "maintenance_days_before_check_in",
      maintenance_days_after_checkout: "maintenance_days_after_check_out",
    };

    Object.entries(property).forEach(([key, value]) => {
      if (
        value === undefined ||
        value === null ||
        !newPropertyAddKeysSet.has(key)
      ) {
        return;
      }

      formData.append(keyMap[key] || key, value.toString());
    });

    const url = `/api/v2/rental/properties/${property_slug}`,
      { error } = await PATCH<RentalPropertyResponse>(url, formData);

    if (error) {
      return {
        response: null,
        error,
      };
    }

    return {
      response: null,
      error,
    };
  }

  async updatePocManagers(
    property_slug: string,
    payload: {
      ho_manager_id: number | "";
      housekeeping_manager_id: number | "";
      maintenance_manager_id: number | "";
    },
  ): Promise<FetchResult<void>> {
    const url = `/api/v2/rental/properties/${property_slug}/update_poc`,
      { error } = await PATCH<void>(url, payload);

    if (error) {
      return { response: null, error };
    }

    return { response: null, error: null };
  }

  async listConfigs(
    property_slug: string,
  ): Promise<FetchResult<RentalPropertyConfig[]>> {
    const qp = new URLSearchParams();

    qp.set("property_slug", property_slug);

    const qpM = qp.toString();

    let url = "/api/v2/rental/property_configs";

    if (qpM) {
      url = `${url}?${qpM}`;
    }

    const { response, error } = await GET<RentalPropertyConfigsResponse>(url);

    if (error) {
      return {
        response: null,
        error,
      };
    }

    if (!response) {
      return {
        response: null,
        error: new Error("Unknown error"),
      };
    }

    const formatted = response.property_configs.map((each) => {
      const { id = 0, bedrooms = 0, active = false } = each,
        formatted: RentalPropertyConfig = {
          id: id || 0,
          bedroom_count: bedrooms || 0,
          active: active || false,
        };

      return formatted;
    });

    return {
      response: formatted,
      error,
    };
  }

  async updateConfig(
    property_slug: string,
    config_id: number,
    bedroom_count: number,
    active: boolean,
  ): Promise<FetchResult<void>> {
    const payload = {
      bedrooms: bedroom_count,
      active: active,
    };

    const qp = new URLSearchParams();

    qp.set("property_slug", property_slug);

    const qpM = qp.toString();

    let url = `/api/v2/rental/property_configs/${config_id}`;

    if (qpM) {
      url = `${url}?${qpM}`;
    }

    const { error } = await PATCH<void>(url, payload);

    if (error) {
      return {
        response: null,
        error,
      };
    }

    return {
      response: null,
      error,
    };
  }

  async addConfig(
    property_slug: string,
    bedroom_count: number,
    active: boolean,
  ): Promise<FetchResult<void>> {
    const payload = {
      bedrooms: bedroom_count,
      active: active,
    };

    const qp = new URLSearchParams();

    qp.set("property_slug", property_slug);

    const qpM = qp.toString();

    let url = `/api/v2/rental/property_configs`;

    if (qpM) {
      url = `${url}?${qpM}`;
    }

    const { error } = await POST<void>(url, payload);

    if (error) {
      return {
        response: null,
        error,
      };
    }

    return {
      response: null,
      error,
    };
  }

  async listPropertyHOApprovals(
    property_slug: string,
    query?: RentalPropertyHOApprovalsListQuery,
  ): Promise<FetchResult<RentalPropertyHOApprovalsListPayload>> {
    const qs = buildApprovalsQueryString(query),
      url = `/api/v2/rental/properties/${encodeURIComponent(
        property_slug,
      )}/approvals${qs}`,
      { error, response } = await GET<unknown>(url);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    if (response === null || response === undefined) {
      return {
        error: new Error("Unknown error"),
        response: null,
      };
    }

    return {
      error: null,
      response: normalizeApprovalsListPayload(response),
    };
  }

  async getPropertyChecklistTickets(
    property_slug: string,
    status?: string[],
    page?: string,
  ): Promise<FetchResult<PropertyChecklistListPayload>> {
    const params = new URLSearchParams();
    if (status?.length) {
      for (const value of status) {
        if (value) {
          params.append("status[]", value);
        }
      }
    }
    if (page != null && page !== "") {
      params.set("page", page);
    }
    const qs = params.toString(),
      url = `/api/v2/rental/properties/${encodeURIComponent(
        property_slug,
      )}/concierge_checklist${qs ? `?${qs}` : ""}`,
      { error, response } = await GET<unknown>(url);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    if (response === null || response === undefined) {
      return {
        error: new Error("Unknown error"),
        response: null,
      };
    }

    return {
      error: null,
      response: normalizeChecklistListPayload(response),
    };
  }

  async getPropertyHODocuments(
    property_slug: string,
    document_type?: string,
  ): Promise<FetchResult<RentalDocument[]>> {
    let url = `/api/v2/rental/properties/${encodeURIComponent(
      property_slug,
    )}/home_owner_attachments`;
    const qp = new URLSearchParams();

    if (document_type) {
      qp.set("document_type", document_type);
    }
    const marshaled = qp.toString();
    if (marshaled) {
      url = `${url}?${marshaled}`;
    }

    const { error, response } = await GET<{
      home_owner_attachments: RentalDocument[];
    }>(url);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    if (!response) {
      return {
        error: new Error("Unknown error"),
        response: null,
      };
    }

    const formatted: RentalDocument[] = (
      response.home_owner_attachments || []
    ).map((each) => {
      const { id, type, name, file } = each;

      return {
        id: id,
        type: type || "",
        name: name || "",
        file: file || "",
      };
    });

    return {
      error: null,
      response: formatted,
    };
  }

  async addPropertyHODocument(
    property_slug: string,
    document_type: string,
    document_identifier: string,
    file: File,
  ): Promise<FetchResult<void>> {
    const formData = new FormData();
    formData.set("document_type", document_type);
    formData.set("document_identifier", document_identifier);
    formData.set("file", file);

    const url = `/api/v2/rental/properties/${encodeURIComponent(
        property_slug,
      )}/home_owner_attachments`,
      { error } = await POST<void>(url, formData);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  async deletePropertyHODocument(
    property_slug: string,
    document_id: number,
  ): Promise<FetchResult<void>> {
    const url = `/api/v2/rental/properties/${encodeURIComponent(
        property_slug,
      )}/home_owner_attachments/${document_id}`,
      { error } = await DELETE<void>(url);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  async listOpportunityHOApprovals(
    opportunity_slug: string,
    query?: RentalPropertyHOApprovalsListQuery,
  ): Promise<FetchResult<RentalPropertyHOApprovalsListPayload>> {
    const qs = buildApprovalsQueryString(query),
      url = `/api/v2/rental/opportunities/${encodeURIComponent(
        opportunity_slug,
      )}/approvals${qs}`,
      { error, response } = await GET<unknown>(url);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    if (response === null || response === undefined) {
      return {
        error: new Error("Unknown error"),
        response: null,
      };
    }

    return {
      error: null,
      response: normalizeApprovalsListPayload(response),
    };
  }

  async getPropertyHOApproval(
    property_slug: string,
    approval_slug: string,
  ): Promise<FetchResult<RentalPropertyHOApprovalDetail>> {
    const url = `/api/v2/rental/properties/${encodeURIComponent(
        property_slug,
      )}/approvals/${encodeURIComponent(approval_slug)}`,
      { error, response } = await GET<RentalPropertyHOApprovalDetail>(url);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    if (!response) {
      return {
        error: new Error("Unknown error"),
        response: null,
      };
    }

    const unwrapped = unwrapPropertyHOApprovalDetailPayload(response);
    if (
      !unwrapped ||
      typeof unwrapped !== "object" ||
      Array.isArray(unwrapped)
    ) {
      return {
        error: new Error("Invalid approval detail payload."),
        response: null,
      };
    }

    return {
      error: null,
      response: unwrapped as RentalPropertyHOApprovalDetail,
    };
  }

  async createPropertyHOApproval(
    property_slug: string,
    formData: FormData,
  ): Promise<FetchResult<void>> {
    const url = `/api/v2/rental/properties/${encodeURIComponent(
        property_slug,
      )}/approvals`,
      { error } = await POST<void>(url, formData);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  async approvePropertyHOApproval(
    property_slug: string,
    approval_slug: string,
    comment?: string,
  ): Promise<FetchResult<void>> {
    const url = `/api/v2/rental/properties/${encodeURIComponent(
        property_slug,
      )}/approvals/${encodeURIComponent(approval_slug)}/approve`,
      body =
        comment != null && comment !== ""
          ? (() => {
              const fd = new FormData();
              fd.append("comment", comment);
              return fd;
            })()
          : undefined,
      { error } = await PATCH<void>(url, body);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  async rejectPropertyHOApproval(
    property_slug: string,
    approval_slug: string,
    comment?: string,
  ): Promise<FetchResult<void>> {
    const url = `/api/v2/rental/properties/${encodeURIComponent(
        property_slug,
      )}/approvals/${encodeURIComponent(approval_slug)}/reject`,
      body =
        comment != null && comment !== ""
          ? (() => {
              const fd = new FormData();
              fd.append("comment", comment);
              return fd;
            })()
          : undefined,
      { error } = await PATCH<void>(url, body);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }
}

export default RentalPropertyService;
